import {
    Badge,
    Box,
    Flex,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Text,
    VStack
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi'
import { ParentDirectory, directoryName } from '@/shared/api/hooks/useParentDirectory'
import { ParentDuplicateRecord } from '@/shared/api/rest/services/parent-duplicates.service'
import {
    DuplicateStatus,
    STATUS_COLOR,
    STATUS_LABEL,
    duplicateStatus,
    embeddedPartyName,
    formatDate,
    formatScore,
    shortId
} from '../types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export type DuplicateFilter = 'all' | DuplicateStatus

const FILTERS: { value: DuplicateFilter; label: string }[] = [
    { value: 'pending', label: 'Pending' },
    { value: 'merged', label: 'Merged' },
    { value: 'dismissed', label: 'Not duplicate' },
    { value: 'all', label: 'All' }
]

type Props = {
    duplicates: ParentDuplicateRecord[]
    /** Bulk name lookup — see `useParentDirectory`. */
    directory: ParentDirectory
    directoryLoading?: boolean
    total: number
    counts: Record<DuplicateStatus, number>
    allCount: number
    status: DuplicateFilter
    onStatusChange: (status: DuplicateFilter) => void
    search: string
    onSearch: (value: string) => void
    selectedId: string | null
    onSelect: (id: string) => void
    page: number
    pageCount: number
    onPageChange: (page: number) => void
    pageSize: number
    onPageSizeChange: (size: number) => void
    loading?: boolean
}

/**
 * The first of the two grids: every suspected pair, one row each. Selecting a
 * row drives the side-by-side comparison in the same page.
 */
export default function DuplicateList({
    duplicates,
    directory,
    directoryLoading,
    total,
    counts,
    allCount,
    status,
    onStatusChange,
    search,
    onSearch,
    selectedId,
    onSelect,
    page,
    pageCount,
    onPageChange,
    pageSize,
    onPageSizeChange,
    loading
}: Props) {
    return (
        <Flex direction="column" h="full">
            <Box p={3} borderBottomWidth="1px" borderColor="gray.100">
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <FiSearch color="gray" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search by name, email or phone..."
                        value={search}
                        onChange={(event) => onSearch(event.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>

                <Flex gap={1} mt={2} flexWrap="wrap">
                    {FILTERS.map((filter) => {
                        const count =
                            filter.value === 'all' ? allCount : counts[filter.value]
                        // Keep the chip row short: hide empty states unless selected.
                        if (
                            count === 0 &&
                            filter.value !== 'all' &&
                            status !== filter.value
                        )
                            return null
                        const isActive = status === filter.value
                        return (
                            <Box
                                as="button"
                                key={filter.value}
                                onClick={() => onStatusChange(filter.value)}
                                px={2}
                                py={1}
                                borderRadius="full"
                                borderWidth="1px"
                                fontSize="xs"
                                fontWeight={isActive ? 'semibold' : 'normal'}
                                borderColor={isActive ? 'brand.300' : 'gray.200'}
                                bg={isActive ? 'brand.50' : 'white'}
                                color={isActive ? 'brand.400' : 'gray.600'}
                                _hover={{ bg: isActive ? 'brand.50' : 'gray.50' }}
                                aria-pressed={isActive}
                            >
                                {filter.label} {count}
                            </Box>
                        )
                    })}
                </Flex>

                <Flex align="center" gap={2} mt={2}>
                    <Text fontSize="xs" color="gray.500">
                        {total} {total === 1 ? 'pair' : 'pairs'}
                    </Text>
                    {loading ? <Spinner size="xs" color="gray.400" /> : null}
                </Flex>
            </Box>

            <VStack align="stretch" spacing={0} overflowY="auto" flex="1">
                {duplicates.length === 0 ? (
                    <Text p={4} fontSize="sm" color="gray.500">
                        No suspected duplicates here.
                    </Text>
                ) : (
                    duplicates.map((record) => {
                        const isActive = record._id === selectedId
                        const recordStatus = duplicateStatus(record)
                        const primary = directory[record.parentId]
                        const secondary = directory[record.duplicateId]
                        // Detection pairs on matching name, so one name normally
                        // labels the whole row; the ids disambiguate.
                        //
                        // The row's own embedded parents come first: the server
                        // enriches them from `public.users`, so they name every
                        // pair including the ones past the directory's page cap
                        // — which is what used to leave rows reading "Name
                        // unavailable". The directory is the fallback for a
                        // server that predates that.
                        const name =
                            embeddedPartyName(record) ||
                            directoryName(primary) ||
                            directoryName(secondary) ||
                            (directoryLoading ? 'Loading…' : 'Name unavailable')
                        const childTotal =
                            (primary?.childCount ?? 0) + (secondary?.childCount ?? 0)
                        return (
                            <Flex
                                as="button"
                                key={record._id}
                                textAlign="left"
                                align="center"
                                gap={3}
                                px={4}
                                py={3}
                                borderBottomWidth="1px"
                                borderColor="gray.50"
                                bg={isActive ? 'brand.50' : 'white'}
                                _hover={{ bg: isActive ? 'brand.50' : 'gray.50' }}
                                onClick={() => onSelect(record._id)}
                            >
                                <Box minW={0} flex="1">
                                    <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                                        {name}
                                    </Text>
                                    <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        noOfLines={1}
                                        fontFamily="mono"
                                    >
                                        {shortId(record.parentId)} ↔{' '}
                                        {shortId(record.duplicateId)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                        {formatScore(record.similarityScore)} match
                                        {childTotal > 0
                                            ? ` · ${childTotal} ${
                                                  childTotal === 1 ? 'child' : 'children'
                                              }`
                                            : ''}
                                        {formatDate(record.createdAt)
                                            ? ` · ${formatDate(record.createdAt)}`
                                            : ''}
                                    </Text>
                                </Box>
                                <Badge
                                    colorScheme={STATUS_COLOR[recordStatus]}
                                    borderRadius="full"
                                    px={2}
                                    fontSize="10px"
                                    flexShrink={0}
                                >
                                    {STATUS_LABEL[recordStatus]}
                                </Badge>
                            </Flex>
                        )
                    })
                )}
            </VStack>

            <Flex
                align="center"
                justify="space-between"
                gap={2}
                px={2}
                py={2}
                borderTopWidth="1px"
                borderColor="gray.100"
            >
                <Select
                    size="xs"
                    value={pageSize}
                    onChange={(event) => onPageSizeChange(Number(event.target.value))}
                    w="auto"
                    borderRadius="md"
                    aria-label="Rows per page"
                >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                            {size} / page
                        </option>
                    ))}
                </Select>
                {pageCount > 1 ? (
                    <Flex align="center" gap={1}>
                        <IconButton
                            aria-label="Previous page"
                            icon={<FiChevronLeft />}
                            size="xs"
                            variant="ghost"
                            onClick={() => onPageChange(page - 1)}
                            isDisabled={page <= 1}
                        />
                        <Text fontSize="xs" color="gray.500">
                            Page {page} of {pageCount}
                        </Text>
                        <IconButton
                            aria-label="Next page"
                            icon={<FiChevronRight />}
                            size="xs"
                            variant="ghost"
                            onClick={() => onPageChange(page + 1)}
                            isDisabled={page >= pageCount}
                        />
                    </Flex>
                ) : null}
            </Flex>
        </Flex>
    )
}
